import {QuestionCircleOutlined} from '@ant-design/icons';
import {Tooltip} from 'antd';
import cn from 'classnames';
import React, {useCallback, useMemo} from 'react';

import {DynamicIcon} from 'components/dynamic-icon';
import {useAppHistory} from 'shared/hooks/use-app-history';
import {getPopupContainer} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {
    menuIconResolver,
    selectMenuStructure,
    selectIsMenuOpen,
    selectSelectedMenusIds,
} from 'store/slices/menu-slice';
import {menuSliceActions} from 'store/slices/menu-slice/menu-slice';
import {IMenuStructureItem} from 'store/slices/menu-slice/menu-slice-types';

import {checkIfIsTopLevelMenuItemSelected} from '../new-side-menu-utils';

interface INewSideMenuItemProps {
    item: IMenuStructureItem;
}

export const NewSideMenuItem = (
    {item}: INewSideMenuItemProps,
) => {
    const dispatch = useAppDispatch();

    const history = useAppHistory();

    const menuStructure = useAppSelector(selectMenuStructure);

    const selectedMenusIds = useAppSelector(selectSelectedMenusIds);
    const {
        setSelectedMenuItemsIds,
        setIsMenuOpen,
    } = menuSliceActions;

    const icon = menuIconResolver(item.itemKey) ?? item.icon ?? undefined;

    const isMenuOpen = useAppSelector(selectIsMenuOpen);

    const isSelected = useMemo(() => checkIfIsTopLevelMenuItemSelected({
        item,
        menuStructure,
        pathname: history.location.pathname,
    }), [item, menuStructure, history.location.pathname]);

    const handleClick = useCallback(() => {
        if (selectedMenusIds.includes(item.id)) {
            dispatch(setIsMenuOpen(!isMenuOpen));
        } else {
            dispatch(setIsMenuOpen(true));
            dispatch(setSelectedMenuItemsIds([item.id]));
        }
    }, [item, selectedMenusIds, isMenuOpen]);

    const contentIcon = icon ? (
        <DynamicIcon
            type={icon}
        />
    ) : <QuestionCircleOutlined />;

    return (
        <div
            key={item.id}
            className={cn(
                'new-side-menu__content__item',
                {
                    'new-side-menu__content__item_selected': (
                        selectedMenusIds.includes(item.id) || isSelected
                    ),
                },
            )}
            onClick={handleClick}
        >
            {isMenuOpen ? contentIcon : (
                <Tooltip
                    placement="right"
                    title={item.itemTitle}
                    getPopupContainer={getPopupContainer()}
                    overlayClassName="new-side-menu__tooltip"
                    mouseEnterDelay={0.4}
                >
                    {contentIcon}
                </Tooltip>
            )}
        </div>
    );
};
